import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import { getFileMetadata } from "../../visualization/LeadTimeHistogram/api/leadTimeHistogramApi";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";
import { formatCompactNumber } from "../../../utils/numberFormat";

const OBJECT_COUNT_VALUE_MAX_PX = 60; // was text-6xl
const OBJECT_COUNT_VALUE_MIN_PX = 20;

// Extracted so useAutoFitFontSize is called from a real component's render, not from the nested
// renderKPICard() helper — hooks called from a non-component helper violate the Rules of Hooks.
// This is a raw object count, so it gets the compact ("1,2 Mio.") fallback for extreme cases.
const ObjectCountBigValue = ({ count }) => {
  const text = count !== null && count !== undefined ? count.toLocaleString() : "—";
  const compactText = typeof count === "number" ? formatCompactNumber(count) : undefined;
  const { ref, fontSizePx, displayText, isCompact } = useAutoFitFontSize({
    text,
    maxPx: OBJECT_COUNT_VALUE_MAX_PX,
    minPx: OBJECT_COUNT_VALUE_MIN_PX,
    compactText,
  });

  return (
    <div
      ref={ref}
      className="max-w-full truncate font-bold mb-2 text-primary-500"
      style={{ fontSize: `${fontSizePx}px` }}
      title={isCompact ? text : undefined}
    >
      {displayText}
    </div>
  );
};

const ObjectCountsBySelectedActivities = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
}) => {
  const api = useGlobalAPI(activeFile);

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Form state
  const [objectType, setObjectType] = useState("");
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [mode, setMode] = useState("OR");
  const [kpiTitle, setKpiTitle] = useState("Object Count by Activity");

  // Settings panel state
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(() => {
    const saved = localStorage.getItem(
      "objectCountByActivityKPI_settingsCollapsed"
    );
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Metadata state
  const [fileMetadata, setFileMetadata] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [activityTypesLoading, setActivityTypesLoading] = useState(false);

  // Track if activities have been fetched for current file
  const activitiesFetchedRef = useRef(null);
  // Track previous filters to detect actual changes
  const prevFiltersRef = useRef(null);

  // Save settings state to localStorage
  useEffect(() => {
    localStorage.setItem(
      "objectCountByActivityKPI_settingsCollapsed",
      JSON.stringify(isSettingsCollapsed)
    );
  }, [isSettingsCollapsed]);

  // Reset state when activeFile changes
  useEffect(() => {
    setData(null);
    setLoading(false);
    setError(null);
    setHasFetched(false);
    setObjectType("");
    setSelectedActivities([]);
    setMode("OR");
    setKpiTitle("Object Count by Activity");
    setFileMetadata(null);
    setObjectTypes([]);
    setObjectTypeActivities({});
    activitiesFetchedRef.current = null;
    prevFiltersRef.current = null;
  }, [activeFile]);

  // Fetch metadata (object types)
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeFile) {
        setObjectTypes([]);
        return;
      }

      try {
        console.log(
          "🔄 Fetching metadata for ObjectCountsBySelectedActivities:",
          activeFile
        );
        const metadata = await getFileMetadata(activeFile);
        setFileMetadata(metadata);

        if (metadata?.object_types) {
          const types = Object.keys(metadata.object_types);
          setObjectTypes(types);
          console.log(
            "✅ Object types loaded for ObjectCountsBySelectedActivities:",
            types
          );
        } else {
          setObjectTypes(["Auftrag", "Angebot", "Lieferschein"]);
        }
      } catch (error) {
        console.error(
          "❌ Error fetching metadata for ObjectCountsBySelectedActivities:",
          error
        );
        setObjectTypes(["Auftrag", "Angebot", "Lieferschein"]);
      }
    };

    fetchMetadata();
  }, [activeFile]);

  // Fetch object-type activities
  useEffect(() => {
    const fetchObjectTypeActivities = async () => {
      if (!activeFile) {
        setObjectTypeActivities({});
        return;
      }

      // Skip if already fetched for this file
      if (activitiesFetchedRef.current === activeFile) {
        console.log(
          "⏭️ Skipping activities fetch - already loaded for",
          activeFile
        );
        return;
      }

      setActivityTypesLoading(true);
      try {
        console.log("🔄 Fetching object-type activities for:", activeFile);
        const response = await api.get(
          "/object-type-activities",
          {},
          "object type activities"
        );

        if (response?.objectTypeActivities) {
          setObjectTypeActivities(response.objectTypeActivities);
          activitiesFetchedRef.current = activeFile;
          console.log(
            "✅ Object-type activities loaded:",
            response.objectTypeActivities
          );
        } else {
          console.warn("⚠️ No objectTypeActivities in response");
          setObjectTypeActivities({});
        }
      } catch (error) {
        console.error("❌ Error fetching object-type activities:", error);
        setObjectTypeActivities({});
      } finally {
        setActivityTypesLoading(false);
      }
    };

    fetchObjectTypeActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile]);

  // Reset activities when object type changes
  useEffect(() => {
    console.log("🔄 Object type changed to:", objectType);
    setSelectedActivities([]);
  }, [objectType]);

  // Form validation helper
  const isFormValid = useCallback(() => {
    return (
      objectType &&
      selectedActivities.length > 0 &&
      kpiTitle &&
      !activityTypesLoading
    );
  }, [objectType, selectedActivities.length, kpiTitle, activityTypesLoading]);

  const toggleSettings = useCallback(() => {
    console.log("🔧 ObjectCountsBySelectedActivities: Toggle settings clicked");
    setIsSettingsCollapsed((prev) => !prev);
  }, []);

  const fetchKPIData = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    // Validate form before making request
    if (!objectType || selectedActivities.length === 0) {
      if (hasFetched) {
        setError("Please select an object type and at least one activity");
      }
      return;
    }

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("🔄 Fetching object count by activity KPI data...", {
        activeFile,
        objectType,
        activities: selectedActivities,
        mode,
      });

      // Use global API - filters are automatically merged
      const response = await api.post(
        "/kpi/object-count-by-activity",
        {
          object_type: objectType,
          activities: selectedActivities,
          mode: mode,
        },
        {},
        "Object Count by Activity KPI"
      );

      console.log(
        "📥 Object Count by Activity KPI response received:",
        response
      );

      setData(response);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("❌ Error during KPI data fetch:", err);
      setError(err.message || "An unknown error occurred.");
      setData(null); // Clear data on error
    } finally {
      setLoading(false);
    }
  }, [activeFile, objectType, selectedActivities, mode, api, hasFetched]);

  // Auto-reload ONLY when global filters ACTUALLY change (not on every render)
  useEffect(() => {
    // Only trigger if we have previously fetched data
    if (!hasFetched || loading) {
      return;
    }

    // Check if form is still valid
    if (!isFormValid()) {
      return;
    }

    // Only refetch if filters actually changed
    if (api.currentFilters) {
      const filtersChanged =
        prevFiltersRef.current === null ||
        JSON.stringify(prevFiltersRef.current) !==
          JSON.stringify(api.currentFilters);

      if (filtersChanged) {
        console.log("🔄 Global filters changed, refetching KPI data");
        prevFiltersRef.current = api.currentFilters;
        fetchKPIData();
      } else {
        console.log("⏭️ Filters unchanged, skipping refetch");
        prevFiltersRef.current = api.currentFilters;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.currentFilters]);

  const hasData = !loading && !error && data !== null;
  const showInitialButton = !hasFetched && !loading;

  // Settings toggle button for header - memoized to prevent unnecessary re-renders
  const settingsButton = useMemo(
    () => (
      <button
        onClick={toggleSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-expanded={!isSettingsCollapsed}
        aria-label={isSettingsCollapsed ? "Show settings" : "Hide settings"}
      >
        <svg
          className={`w-3 h-3 transition-transform duration-150 ${
            isSettingsCollapsed ? "" : "rotate-180"
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">Settings</span>
      </button>
    ),
    [isSettingsCollapsed, toggleSettings]
  );

  // Handle Enter key press on form inputs
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // Trigger fetch if form is valid
        if (isFormValid()) {
          fetchKPIData();
        }
      }
    },
    [isFormValid, fetchKPIData]
  );

  // Provide refresh function to parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchKPIData);
    }
  }, [onRefresh, fetchKPIData]);

  // Provide header actions to parent (GridComponent)
  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(settingsButton);
    }
  }, [onGetHeaderActions, settingsButton]);

  const renderKPICard = () => {
    if (!data) {
      console.warn("⚠️ renderKPICard called but data is null");
      return null;
    }

    console.log("✅ Rendering KPI card with data:", data);

    return (
      <div className="flex flex-col gap-4 w-full">
        {/* KPI Title - Display custom title */}
        {kpiTitle && kpiTitle !== "Object Count by Activity" && (
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800">{kpiTitle}</h3>
          </div>
        )}

        <div className="rounded-md p-4 flex-1 flex flex-col items-center justify-center text-center min-h-[120px]">
          <ObjectCountBigValue count={data.object_count} />
          <div className="text-lg font-medium text-gray-700 mb-1">
            {data.object_type} Objects
          </div>
        </div>
      </div>
    );
  };

  console.log("🎨 ObjectCountsBySelectedActivities render - State:", {
    hasData,
    loading,
    error,
    hasFetched,
    dataExists: !!data,
    showInitialButton,
    objectType,
    selectedActivities: selectedActivities.length,
  });

  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && isSettingsCollapsed && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsSettingsCollapsed(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* Settings Panel */}
        {!isSettingsCollapsed && (
          <div className="transition-all duration-200 ease-in-out mb-4">
            <div className="space-y-4 pb-4 border-b border-gray-200">
              {/* KPI Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  KPI Title *
                </label>
                <input
                  type="text"
                  value={kpiTitle}
                  onChange={(e) => setKpiTitle(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Orders with Confirmation"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Custom name for your KPI widget
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Object Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Object Type *
                  </label>
                  <select
                    value={objectType}
                    onChange={(e) => setObjectType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  >
                    <option value="">Select Object Type...</option>
                    {objectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Type of objects to count
                  </p>
                </div>

                {/* Activities Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activities *
                  </label>
                  <MultiSelectDropdown
                    options={
                      objectType ? objectTypeActivities[objectType] || [] : []
                    }
                    selectedValues={selectedActivities}
                    onSelectionChange={setSelectedActivities}
                    placeholder={
                      objectType
                        ? "Select activities..."
                        : "Select object type first..."
                    }
                    disabled={!objectType || loading || activityTypesLoading}
                    itemType="activities"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {objectType
                      ? "Activities to filter by"
                      : "Select an object type to view activities"}
                  </p>
                </div>

                {/* Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode *
                  </label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  >
                    <option value="OR">OR (At least one activity)</option>
                    <option value="AND">AND (All activities)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Logical operation to apply
                  </p>
                </div>
              </div>

              {/* Calculate KPI Button */}
              <div className="flex justify-center pt-2">
                <button
                  onClick={fetchKPIData}
                  disabled={!activeFile || !isFormValid() || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading
                    ? "Calculating..."
                    : hasFetched
                    ? "Update KPI"
                    : "Calculate KPI"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KPI Results */}
        {hasData && <div className="pt-2">{renderKPICard()}</div>}

        {/* Placeholder when settings are collapsed and no data */}
        {!hasData &&
          !loading &&
          !error &&
          !showInitialButton &&
          isSettingsCollapsed && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-6xl font-bold mb-2 text-gray-300">—</div>
                <div className="text-lg font-medium text-gray-500">
                  Object Count
                </div>
                <div className="text-sm text-gray-400 mt-2">
                  {hasFetched
                    ? "No data available"
                    : "Configure settings to calculate"}
                </div>
              </div>
            </div>
          )}

        {/* No Data Message */}
        {hasFetched &&
          !loading &&
          !error &&
          !hasData &&
          !isSettingsCollapsed && (
            <TileStatusContent
              status="empty"
              title="No Data Found"
              message="No objects were found matching the specified activity criteria. Try adjusting your selections or filters."
              action={
                <button
                  onClick={fetchKPIData}
                  className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Retry
                </button>
              }
            />
          )}

        {/* Error State with Retry */}
        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchKPIData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default ObjectCountsBySelectedActivities;
